import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { api } from "../api.ts";
import { ApiError } from "../api-errors.ts";
import { CONSOLE_PATH, navigate, type Route, useConsoleRoute } from "../router.tsx";
import { BuildRefresh } from "../shell/BuildRefresh.tsx";
import { SignIn, StandaloneState } from "../shell/SignIn.tsx";
import { useForgetOnIdentityChange, useRefreshOnRenewal } from "../shell/session-cache.ts";
import { useSessionWatch } from "../shell/session-watch.ts";
import { ThemeProvider } from "../theme/theme.tsx";
import { ConsoleApp } from "./ConsoleApp.tsx";
import { OperatorSessionProvider } from "./operator.tsx";

/**
 * The console website's page (openspec change operator-identity-pool, D6: `console.html`, its
 * own origin): an operator's session opens the console, anything else gets the console's own
 * sign-in, which signs operators in only. Nothing of the tenant application is mounted here.
 */
export function ConsoleRoot() {
  // Re-renders the whole tree when the language changes, so every string follows it.
  useTranslation();
  return (
    <ThemeProvider>
      <TooltipProvider delayDuration={300}>
        <ConsoleSigned />
        <Toaster position="bottom-right" />
        <BuildRefresh />
      </TooltipProvider>
    </ThemeProvider>
  );
}

function ConsoleSigned() {
  const { t } = useTranslation();
  const route = useConsoleRoute();
  // The browser holds only the console's httpOnly session cookie; this tells whether it is live.
  const session = useQuery({
    queryKey: ["session"],
    queryFn: async () => (await api.api.auth.session.$get()).json(),
    retry: 1,
  });
  const operatorId =
    session.data?.authenticated && session.data.principal.type === "operator"
      ? session.data.principal.operatorId
      : null;
  // Another operator signed in elsewhere: nothing of the previous one's console stays cached
  // (grants, sessions); a token rotated in another tab is fetched again.
  useForgetOnIdentityChange(operatorId);
  useRefreshOnRenewal();
  if (session.isPending) {
    return null;
  }
  if (session.isError) {
    const network = !(session.error instanceof ApiError);
    return (
      <StandaloneState console title={t("app.unreachable")}>
        <Notice
          tone="danger"
          action={
            <Button size="sm" variant="outline" onClick={() => void session.refetch()}>
              {t("app.retry")}
            </Button>
          }
        >
          {network ? t("errors.network") : session.error.message}
        </Notice>
      </StandaloneState>
    );
  }
  const principal = session.data.authenticated ? session.data.principal : null;
  if (session.data.authenticated && principal?.type === "operator") {
    return (
      <OperatorSessionProvider
        key={principal.operatorId}
        value={{
          csrfToken: session.data.csrfToken,
          operatorId: principal.operatorId,
          email: principal.email ?? null,
          name: principal.name ?? null,
        }}
      >
        <OperatorWatch />
        <OperatorConsole route={route} />
      </OperatorSessionProvider>
    );
  }
  // Signed out, or a session that is not an operator's: the console's own sign-in, back to the
  // console address that was asked for.
  return <SignIn realm="operator" />;
}

/**
 * Operators only ever use the console: any other path lands on its tenant list (the address
 * bar says so too).
 */
function OperatorConsole({ route }: { route: Route }) {
  const onConsole = route.name === "console";
  // A console address that moved (a tab's former path, a former `/console/...` address, #390)
  // keeps its destination, query included.
  const target = route.name === "redirect" ? route.to : CONSOLE_PATH;
  useEffect(() => {
    if (!onConsole) {
      navigate(target, { replace: true });
    }
  }, [onConsole, target]);
  if (route.name === "redirect" && target !== CONSOLE_PATH) {
    // On its way to the moved address: no tenant list in between.
    return null;
  }
  return <ConsoleApp view={route.name === "console" ? route.view : { name: "tenants" }} />;
}

/** Watches an operator's session like a user's (a removed operator's ends at once). */
function OperatorWatch() {
  useSessionWatch();
  return null;
}
